import { ProductPlacement } from './productPlacement';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import Image from 'next/image';
import { ProductBigPreviewProps } from './types';
import { ProductPrice } from './productPrice';
import { AdViewSource } from '@/utils/adViewSource';
import { AdViewSourceLink } from './AdViewSourceLink';
import { fixed340Loader } from '@/utils/imageLoaders/imageLoader340';
import { fixed800Loader } from '@/utils/imageLoaders/imageLoader800';
import { ProductBigPreviewClient } from './productBigPreview.client';
import { ProductBadgesClient } from './productBadges.client';
import { PhotoCountBadge } from './photoCountBadge';
import { runtimeConfig } from '@/config/runtime-config';

const blurBase64 = 'data:image/webp;base64,UklGRioAAABXRUJQVlA4IB4AAAAwAQCdASoKAAoAAgA0JaQAA3AA/vFEZpjUTzWAgAA=';

const isTGMiniApp = runtimeConfig.domain.toLowerCase().startsWith('tg');

export function ProductBigPreview({
  product,
  photoBottomLeftSection,
  imgClassname = '',
  eventName,
  showFavorite = true,
  getFavoriteFromProduct = true,
  customIsFavorite = false,
  adViewSource,
  priority = false,
  forceSquare = false,
}: ProductBigPreviewProps) {
  const favoriteValue = getFavoriteFromProduct ? Boolean(product.isFavourite) : Boolean(customIsFavorite);
  const isPremium = product.isPremium;
  const isWide = isPremium && !forceSquare;
  return (
    <div
      className='flex flex-col justify-start relative cursor-pointer rounded-[12px] w-full min-w-0'
      data-product-card-root='true'
      key={product.id}
    >
      <AdViewSourceLink
        title={product.name}
        target={isTGMiniApp ? '_self' : '_blank'}
        rel={isTGMiniApp ? '' : 'noopener noreferrer'}
        className='absolute z-10 top-0 start-0 end-0 bottom-0'
        href={`/a/${product.link}`}
        adViewSource={adViewSource as AdViewSource}
      />

      <ProductBadgesClient
        isPremium={isPremium}
        isFree={product.priceMode === 'FREE'}
        discountPercent={product.discountPercent}
        isBargain={product.isBargain}
        isUrgentSale={product.isUrgentSale}
      />

      <div
        className={twMerge(
          'relative shrink-0 bg-gray-300 rounded-[12px] overflow-hidden w-full aspect-square border border-solid border-bg2-day',
          imgClassname,
          isWide ? 'aspect-2/1' : '',
        )}
      >
        {product.photos?.length > 0 && (
          <Image
            fill
            priority={priority}
            sizes='320px'
            loader={isWide ? fixed800Loader : fixed340Loader}
            placeholder='blur'
            blurDataURL={blurBase64}
            quality={75}
            className='size-full object-cover'
            src={product.photos[0]}
            alt={product.name}
          />
        )}

        {(photoBottomLeftSection || product.photos.length > 1) && (
          <div className='absolute bottom-[8px] start-[8px]'>
            {photoBottomLeftSection ?? <PhotoCountBadge count={product.photos.length} />}
          </div>
        )}

        {(showFavorite || eventName) && (
          <ProductBigPreviewClient
            eventName={eventName}
            showFavorite={showFavorite}
            productId={product.id}
            productOwnerId={product.telegramUserId}
            productLink={product.link}
            isFavourite={favoriteValue}
          />
        )}
      </div>

      <div className='flex flex-col gap-[3px] text-text-primary'>
        <div className='flex gap-[4px] min-w-0 justify-between items-start mt-[10px]'>
          <p className='line-clamp-2 leading-[20px] text-[14px] font-normal'>{product.name}</p>
        </div>
        {product.priceMode !== 'NEGOTIABLE' && (
          <p
            className={clsx(
              'min-w-0 text-[16px] text-text-primary-day leading-[20px] overflow-hidden text-ellipsis font-semibold',
            )}
          >
            <ProductPrice product={product} />
          </p>
        )}
        <p className='line-clamp-1 break-all text-[14px] text-text-disabled-day'>
          <ProductPlacement product={product} />
        </p>
      </div>
    </div>
  );
}
